import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, Typography, message, Spin, Switch, Space } from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { createProject, updateProject, getProjectById, getFormConfigById, Project } from '../../services/api';
import { DynamicForm, FormConfig, FormFieldConfig } from '../../components/DynamicForm';
import './styles.css';

const { Title } = Typography;
const { TextArea } = Input;

interface ProjectFormProps {
  mode: 'create' | 'edit';
}

const ProjectForm: React.FC<ProjectFormProps> = ({ mode }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formConfig, setFormConfig] = useState<FormConfig | null>(null);
  const [dynamicFields, setDynamicFields] = useState<Record<string, any>>({});
  const navigate = useNavigate();

  const { id } = useParams<{ id: string }>();
  const isEditMode = mode === 'edit';
  const projectId = isEditMode ? Number(id) : undefined;

  useEffect(() => {
    if (isEditMode && projectId) {
      fetchProjectDetails(projectId);
    }
  }, [isEditMode, projectId]);

  // 获取默认表单字段配置
  const getDefaultFormFields = (): FormFieldConfig[] => {
    return [
      {
        key: 'name',
        label: '协议代码',
        type: 'input',
        required: true,
        placeholder: '请输入协议代码',
        rules: [{ required: true, message: '请输入协议代码' }]
      },
      {
        key: 'title',
        label: '协议名称',
        type: 'input',
        required: true,
        placeholder: '请输入协议名称',
        rules: [{ required: true, message: '请输入协议名称' }]
      },
      {
        key: 'projectCode',
        label: '项目代码',
        type: 'input',
        required: true,
        placeholder: '请输入项目代码',
        rules: [{ required: true, message: '请输入项目代码' }]
      },
      {
        key: 'region',
        label: '区域',
        type: 'input',
        required: true,
        placeholder: '请输入区域',
        rules: [{ required: true, message: '请输入区域' }]
      },
      {
        key: 'appKey',
        label: 'AppKey',
        type: 'input',
        required: false,
        placeholder: '请输入AppKey',
        rules: [{ required: true, message: '请输入AppKey' }]
      },
      {
        key: 'appSecret',
        label: 'AppSecret',
        type: 'input',
        required: false,
        placeholder: '请输入AppSecret',
        rules: [{ required: true, message: '请输入AppSecret' }]
      },
      {
        key: 'status',
        label: '状态',
        type: 'switch',
        required: false,
        defaultValue: true
      }
    ];
  };

  // 创建默认表单配置
  const getDefaultFormConfig = (): FormConfig => {
    return {
      id: 'default_project_form',
      name: '协议表单',
      fields: getDefaultFormFields()
    };
  };

  const fetchProjectDetails = async (id: number) => {
    try {
      setLoading(true);
      const response = await getProjectById(id);
      if (response.data.code === 0 && response.data.data) {
        const projectData = response.data.data;
        
        // 设置基本表单字段
        form.setFieldsValue({
          name: projectData.name,
          title: projectData.title,
          projectCode: projectData.projectCode,
          region: projectData.region,
          appKey: projectData.appKey,
          appSecret: projectData.appSecret,
          status: projectData.status === 1,
        });

        // 如果有扩展数据，设置到动态字段中
        if (projectData.exData) {
          setDynamicFields(projectData.exData);
          form.setFieldsValue(projectData.exData);
        }

        // 获取动态表单配置
        fetchFormConfig(id);
      } else {
        message.error(response.data.msg || '获取协议详情失败');
        navigate('/projects');
      }
    } catch (error) {
      console.error('获取协议详情错误:', error);
      message.error('获取协议详情失败，请检查网络连接后重试');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };

  // 获取动态表单配置
  const fetchFormConfig = async (projectId: number) => {
    try {
      const formId = `project_${projectId}`;
      const response = await getFormConfigById(formId);
      
      if (response.data.code === 0 && response.data.data) {
        // 合并默认表单字段和动态表单字段
        const defaultFields = getDefaultFormFields();
        const dynamicFields = response.data.data.fields || [];
        
        // 创建一个映射来跟踪已经存在的字段键
        const fieldKeyMap = new Map<string, boolean>();
        defaultFields.forEach(field => fieldKeyMap.set(field.key, true));
        
        // 过滤掉与默认字段重复的动态字段
        const uniqueDynamicFields = dynamicFields.filter(field => !fieldKeyMap.has(field.key));
        
        // 合并字段
        const mergedConfig: FormConfig = {
          ...response.data.data,
          fields: [...defaultFields, ...uniqueDynamicFields]
        };
        
        setFormConfig(mergedConfig);
      } else {
        // 如果没有找到配置，使用默认配置
        setFormConfig(getDefaultFormConfig());
      }
    } catch (error) {
      console.error('获取表单配置错误:', error);
      // 如果获取失败，使用默认配置
      setFormConfig(getDefaultFormConfig());
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      setSubmitting(true);
      
      // 获取所有默认字段的键
      const defaultFieldKeys = getDefaultFormFields().map(field => field.key);
      
      // 分离默认字段和扩展字段
      const defaultValues: Record<string, any> = {};
      const exDataValues: Record<string, any> = {};
      
      // 遍历所有表单值
      Object.entries(values).forEach(([key, value]) => {
        if (defaultFieldKeys.includes(key)) {
          // 如果是默认字段，放入defaultValues
          defaultValues[key] = value;
        } else {
          // 否则放入exDataValues
          exDataValues[key] = value;
        }
      });
      
      // 构建项目数据，包括状态转换和扩展数据
      const projectData = {
        ...defaultValues,
        status: defaultValues.status ? 1 : 0,
        exData: exDataValues
      };
      
      let response;
      
      if (isEditMode && projectId) {
        response = await updateProject(projectId, projectData);
      } else {
        response = await createProject(projectData);
      }
      
      if (response.data.code === 0) {
        message.success(isEditMode ? '协议更新成功' : '协议创建成功');
        navigate('/projects');
      } else {
        message.error(response.data.msg || (isEditMode ? '更新协议失败' : '创建协议失败'));
      }
    } catch (error) {
      console.error(isEditMode ? '更新协议错误:' : '创建协议错误:', error);
      message.error(isEditMode ? '更新协议失败，请检查网络连接后重试' : '创建协议失败，请检查网络连接后重试');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="project-form-container">
      <Card className="project-form-card">
        <div className="project-form-header">
          <Title level={4}>{isEditMode ? '编辑协议' : '创建协议'}</Title>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/projects')}
          >
            返回列表
          </Button>
        </div>
        
        <Spin spinning={loading}>
          {formConfig ? (
            <DynamicForm
              form={form}
              config={formConfig}
              initialValues={{
                status: true,
                ...dynamicFields
              }}
              onFinish={handleSubmit}
              onCancel={() => navigate('/projects')}
              loading={submitting}
              mode={mode === 'create' ? 'create' : 'edit'}
            />
          ) : (
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              initialValues={{ status: true }}
              className="project-form"
            >
              <Form.Item
                name="name"
                label="协议代码"
                rules={[{ required: true, message: '请输入协议代码' }]}
              >
                <Input placeholder="请输入协议代码" />
              </Form.Item>
              
              <Form.Item
                name="title"
                label="协议名称"
                rules={[{ required: true, message: '请输入协议名称' }]}
              >
                <Input placeholder="请输入协议名称" />
              </Form.Item>
              
              <Form.Item
                name="projectCode"
                label="项目代码"
                rules={[{ required: true, message: '请输入项目代码' }]}
              >
                <Input placeholder="请输入项目代码" />
              </Form.Item>
              
              <Form.Item
                name="region"
                label="区域"
                rules={[{ required: true, message: '请输入区域' }]}
              >
                <Input placeholder="请输入区域" />
              </Form.Item>
              
              <Form.Item
                name="appKey"
                label="AppKey"
                rules={[{ required: true, message: '请输入AppKey' }]}
              >
                <Input placeholder="请输入AppKey" />
              </Form.Item>
              
              <Form.Item
                name="appSecret"
                label="AppSecret"
                rules={[{ required: true, message: '请输入AppSecret' }]}
              >
                <Input placeholder="请输入AppSecret" />
              </Form.Item>
              
              <Form.Item
                name="status"
                label="状态"
                valuePropName="checked"
              >
                <Switch checkedChildren="启用" unCheckedChildren="禁用" />
              </Form.Item>
              
              <Form.Item className="form-actions">
                <Space>
                  <Button onClick={() => navigate('/projects')}>取消</Button>
                  <Button 
                    type="primary" 
                    htmlType="submit" 
                    loading={submitting}
                    icon={<SaveOutlined />}
                  >
                    {isEditMode ? '更新' : '创建'}
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          )}
        </Spin>
      </Card>
    </div>
  );
};

export default ProjectForm; 